import type {
  AppStatsResponse,
  AppWithStats,
  Card,
  CefrLevel,
  CoverageTier,
  EinbuergerungsConfig,
  EinbuergerungsPayload,
  ImportCommitResponse,
  ImportPreviewResponse,
  ImportRow,
  KonjugationFieldStats,
  OptionLetter,
  OverallStats,
  OverallStreak,
  SessionAttempt,
  SessionFinishResponse,
  SessionMode,
  StrugglingResponse,
  SessionStartResponse,
  SettingsResponse,
  SimulationPreset,
  StreakPause,
} from '@/core/types';

import type { GrammatikLesson, GrammatikSession, GrammatikState } from '@/apps/grammatik/types';
import type { GrammarBookmark, GrammarRating, GrammarScores } from '@/apps/shared/grammar/types';

const API_BASE = '/api';

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function fetchJson<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: 'include',
    ...init,
    headers: {
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...init?.headers,
    },
  });
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const body = (await res.json()) as { error?: string };
      if (body.error) detail = body.error;
    } catch {
      // ignore
    }
    throw new ApiError(res.status, `${path} → ${res.status} ${detail}`);
  }
  return res.json() as Promise<T>;
}

export const api = {
  // Read endpoints
  apps: () => fetchJson<{ apps: AppWithStats[] }>('/apps'),
  overallStreak: () => fetchJson<OverallStreak>('/streak/overall'),
  overallStats: () => fetchJson<OverallStats>('/stats/overall'),

  // Session lifecycle
  startSession: (params: {
    app_id: string;
    mode: SessionMode;
    count?: number;
    /** Strict CEFR filter. Empty/undefined → practice picks lowest-unmemorized level. */
    levels?: CefrLevel[];
    /** Strict coverage-tier filter for catalog vocabulary apps. Never defaulted. */
    tiers?: CoverageTier[];
  }) =>
    fetchJson<SessionStartResponse>('/sessions', {
      method: 'POST',
      body: JSON.stringify(params),
    }),

  recordAttempt: (sessionId: number, attempt: SessionAttempt) =>
    fetchJson<{ ok: true }>(`/sessions/${sessionId}/attempt`, {
      method: 'POST',
      body: JSON.stringify(attempt),
    }),

  finishSession: (sessionId: number) =>
    fetchJson<SessionFinishResponse>(`/sessions/${sessionId}/finish`, {
      method: 'POST',
    }),

  // The grammar courses — the daily lesson.
  //
  // Their own start endpoint because the deck is ordered rather than sampled:
  // three exercises from yesterday, six from today, one or two spaced. Finish
  // still goes through `finishSession`; `grammarAdvance` is the extra write
  // that moves the curriculum on, and is called after it.
  //
  // Every call takes the app id, because two courses now run on these routes:
  // `grammatik` (German) and `gramatica` (Brazilian Portuguese). The response
  // shapes are identical, so the type parameter carries whichever the caller
  // expects.
  grammarState: <T = GrammatikState>(appId: string) => fetchJson<T>(`/${appId}/state`),

  grammarLesson: <T = GrammatikLesson>(appId: string, n: number) =>
    fetchJson<T>(`/${appId}/lessons/${n}`),

  grammarStartSession: <T = GrammatikSession>(appId: string) =>
    fetchJson<T>(`/${appId}/sessions`, { method: 'POST' }),

  /**
   * Re-drill one lesson, on demand.
   *
   * Runs in `mode: 'lesson'`, which logs attempts and moves card state exactly
   * like practice but is NOT streak-eligible — see the route for why a replayable
   * lesson must not be able to satisfy the grammar requirement.
   */
  grammarPracticeLesson: <T = GrammatikSession>(appId: string, n: number) =>
    fetchJson<T>(`/${appId}/lessons/${n}/practice`, { method: 'POST' }),

  /** Per-lesson accuracy, coverage and struggling counts for a whole course. */
  grammarScores: (appId: string) => fetchJson<GrammarScores>(`/${appId}/scores`),

  /** Per-lesson self-ratings, rolled up: current, first, and the history between. */
  grammarRatings: (appId: string) => fetchJson<{ ratings: GrammarRating[] }>(`/${appId}/ratings`),

  /**
   * Rate a lesson 1–5. Idempotent within a day, so a mis-tap is corrected by
   * tapping again rather than leaving a wrong entry in your own history.
   */
  saveGrammarRating: (appId: string, n: number, stars: number, sessionId?: number) =>
    fetchJson<{ lesson_n: number; stars: number; rated_on: string }>(
      `/${appId}/lessons/${n}/rating`,
      {
        method: 'PUT',
        body: JSON.stringify({ stars, ...(sessionId ? { session_id: sessionId } : {}) }),
      },
    ),

  /** Drill a whole topic — every finished lesson in one curriculum block. */
  grammarPracticeTopic: <T = GrammatikSession>(appId: string, block: number) =>
    fetchJson<T>(`/${appId}/topics/${block}/practice`, { method: 'POST' }),

  grammarBookmarks: (appId: string) =>
    fetchJson<{ bookmarks: GrammarBookmark[] }>(`/${appId}/bookmarks`),

  saveGrammarBookmark: (appId: string, n: number, note: string | null) =>
    fetchJson<{ lesson_n: number; note: string | null; bookmarked: true }>(
      `/${appId}/bookmarks/${n}`,
      { method: 'PUT', body: JSON.stringify({ note }) },
    ),

  removeGrammarBookmark: (appId: string, n: number) =>
    fetchJson<{ lesson_n: number; bookmarked: false }>(`/${appId}/bookmarks/${n}`, {
      method: 'DELETE',
    }),

  /**
   * Move the curriculum on by one.
   *
   * Deliberately takes no lesson number — the server works out which lesson was
   * due. The client knows only which lesson's rule it displayed, and on a
   * post-curriculum review that is "the rule you are weakest on" rather than a
   * lesson being completed; sending it marked unstudied lessons done.
   *
   * Safe to call unconditionally after finishing: the server does nothing
   * unless the session was actually completed, so quitting on card two cannot
   * advance past a lesson you did not do.
   */
  grammarAdvance: (appId: string, sessionId: number) =>
    fetchJson<{ advanced: boolean }>(`/${appId}/sessions/${sessionId}/advance`, {
      method: 'POST',
    }),

  // Per-app stats
  appStats: (appId: string) => fetchJson<AppStatsResponse>(`/apps/${appId}/stats`),

  /**
   * Lifetime per-field miss counts for one card — the "where this verb catches
   * you" block on the Konjugation card back. Empty for every other app, whose
   * attempts carry no `detail`.
   */
  cardFieldStats: (cardId: number) =>
    fetchJson<KonjugationFieldStats>(`/cards/${cardId}/field-stats`),

  /**
   * Correct a card's content. Sets `user_edited`, which is what stops the
   * seeder's upsert from overwriting the correction on the next deploy.
   */
  editCard: (
    cardId: number,
    patch: {
      translations?: Record<string, string[]>;
      example?: string | null;
      example_translations?: Record<string, string>;
      warning?: string | null;
    },
  ) =>
    fetchJson<{ ok: true }>(`/cards/${cardId}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  /** The cards currently flagged as "keeps getting me", worst first. */
  strugglingCards: (appId: string) => fetchJson<StrugglingResponse>(`/apps/${appId}/struggling`),

  // Vacation pauses — days the streak is not judged on.
  streakPauses: () => fetchJson<{ pauses: StreakPause[]; today: string }>('/streak/pauses'),

  createStreakPause: (body: {
    from_date: string;
    to_date: string | null;
    reason?: string | null;
  }) =>
    fetchJson<{ pause: StreakPause }>('/streak/pauses', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  updateStreakPause: (id: number, patch: { from_date?: string; to_date?: string | null }) =>
    fetchJson<{ pause: StreakPause }>(`/streak/pauses/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  deleteStreakPause: (id: number) =>
    fetchJson<{ deleted: true }>(`/streak/pauses/${id}`, { method: 'DELETE' }),

  // Settings
  getSettings: () => fetchJson<SettingsResponse>('/settings'),

  saveLanguages: (body: {
    primary_aux_lang: string;
    secondary_aux_lang: string;
    target_langs?: string[];
  }) =>
    fetchJson<{ ok: true }>('/settings/languages', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  saveTimezone: (body: { timezone: string; grace_hours: number }) =>
    fetchJson<{ ok: true }>('/settings/timezone', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  saveStreakSettings: (body: {
    freezes_per_month: number;
    freezes_auto_spend: boolean;
    apps: {
      app_id: string;
      included_in_overall: boolean;
      sessions_required_per_day: number;
    }[];
  }) =>
    fetchJson<{ ok: true }>('/settings/streak', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  saveAppSettings: (body: {
    apps: {
      app_id: string;
      session_length: number;
      archive_threshold_days: number;
      review_session_length: number;
      archive_review_session_length: number;
      unarchive_failure_threshold: number;
    }[];
  }) =>
    fetchJson<{ ok: true }>('/settings/apps', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // Import
  previewImport: (body: { app_ids: string[]; rows: ImportRow[] }) =>
    fetchJson<ImportPreviewResponse>('/import/preview', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  commitImport: (body: { app_ids: string[]; rows: ImportRow[] }) =>
    fetchJson<ImportCommitResponse>('/import/commit', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /** Absolute URL for downloading a CSV template (used as an <a href>). */
  importTemplateUrl: (appId: string) => `${API_BASE}/import/template/${appId}`,

  /** Absolute URL for the full backup (used as an <a href>). */
  exportUrl: () => `${API_BASE}/export`,

  // -------- Bookmarks (generic; einbuergerungstest surfaces them in v1) --------
  toggleBookmark: (cardId: number, bookmarked: boolean) =>
    fetchJson<{ id: number; bookmarked: boolean }>(`/cards/${cardId}/bookmark`, {
      method: 'PATCH',
      body: JSON.stringify({ bookmarked }),
    }),

  /**
   * "I already know this" — user assertion that marks a card memorized at once.
   *
   * Pass `sessionId` when this happens mid-session: the card is skipped rather
   * than answered, so it also comes out of that session's card total and can't
   * drag the reported accuracy down.
   */
  markCardKnown: (cardId: number, sessionId?: number) =>
    fetchJson<{ id: number; memorized: boolean }>(`/cards/${cardId}/known`, {
      method: 'PATCH',
      body: JSON.stringify(sessionId ? { session_id: sessionId } : {}),
    }),

  // -------- Per-app JSON config (Bundesland, sim preset, etc.) --------
  getAppConfig: <T = Record<string, unknown>>(appId: string) =>
    fetchJson<{ app_id: string; config: T | null }>(`/apps/${appId}/config`),

  saveAppConfig: <T extends Record<string, unknown>>(appId: string, config: T | null) =>
    fetchJson<{ ok: true; app_id: string; config: T | null }>(`/apps/${appId}/config`, {
      method: 'PATCH',
      body: JSON.stringify({ config }),
    }),

  // -------- Library (browse all questions for an app) --------
  getLibrary: (
    appId: string,
    filters: {
      status?: 'all' | 'open' | 'learned';
      bookmark?: 'all' | 'only';
      scope?: 'all' | 'general' | 'state';
      state?: string;
      topic?: string;
    } = {},
  ) => {
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(filters)) {
      if (v) params.append(k, v);
    }
    const qs = params.toString();
    return fetchJson<LibraryResponse>(`/apps/${appId}/library${qs ? `?${qs}` : ''}`);
  },

  // -------- Einbürgerungstest simulation lifecycle --------
  einbuergerungstest: {
    startSimulation: (preset?: SimulationPreset) =>
      fetchJson<SimulationStartResponse>('/einbuergerungstest/simulation/start', {
        method: 'POST',
        body: JSON.stringify(preset ? { preset } : {}),
      }),

    getInProgress: () =>
      fetchJson<{ in_progress: SimulationStartResponse | null }>(
        '/einbuergerungstest/simulation/in-progress',
      ),

    submitSimulation: (
      simId: number,
      answers: {
        card_id: number;
        user_letter: OptionLetter | null;
        bookmarked_during_sim?: boolean;
      }[],
    ) =>
      fetchJson<SimulationSubmitResponse>(`/einbuergerungstest/simulation/${simId}/submit`, {
        method: 'POST',
        body: JSON.stringify({ answers }),
      }),

    abandonSimulation: (simId: number) =>
      fetchJson<{ deleted: number }>(`/einbuergerungstest/simulation/${simId}`, {
        method: 'DELETE',
      }),

    getHistory: () =>
      fetchJson<SimulationHistoryResponse>('/einbuergerungstest/simulation/history'),
  },
};

// -----------------------------------------------------------------------------
// Response shapes for the new endpoints
// -----------------------------------------------------------------------------

export type LibraryCard = {
  id: number;
  external_id: string | null;
  de: string;
  payload: Record<string, unknown>;
  streak: number;
  memorized: boolean;
  bookmarked: boolean;
  last_attempt_correct: boolean | null;
  updated_at: string;
  counts: {
    practice_correct: number;
    practice_incorrect: number;
    sim_correct: number;
    sim_incorrect: number;
  };
};

export type LibraryResponse = {
  app_id: string;
  cards: LibraryCard[];
};

export type SimulationStartResponse = {
  sim_id: number;
  preset: SimulationPreset;
  pass_threshold: number;
  total_questions: number;
  bundesland: string | null;
  /** Present on /in-progress responses; absent on /start (the sim just began). */
  started_at?: string;
  cards: Card<EinbuergerungsPayload>[];
};

export type ScoredAnswer = {
  card_id: number;
  external_id: string;
  user_letter: OptionLetter | null;
  correct_letter: OptionLetter;
  correct: boolean;
  bookmarked_during_sim: boolean;
};

export type SimulationSubmitResponse = {
  sim_id: number;
  preset: SimulationPreset;
  pass_threshold: number;
  total_questions: number;
  correct_count: number;
  passed: boolean;
  answers: ScoredAnswer[];
  wrong_cards: Card<EinbuergerungsPayload>[];
};

export type SimulationHistoryEntry = {
  sim_id: number;
  preset: SimulationPreset;
  bundesland: string | null;
  started_at: string;
  finished_at: string;
  total_questions: number;
  correct_count: number;
  passed: boolean;
};

export type SimulationHistoryResponse = {
  history: SimulationHistoryEntry[];
};

// Re-export the einbuergerungstest config shape for ergonomics.
export type { EinbuergerungsConfig };
